import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Select,
  Row,
  Col,
  Card,
  Tag,
  Input,
  Alert,
  Typography,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  UploadOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { AgreementStatus, AgreementTemplate } from '@/types'
import { AgreementRow, agreementStore } from './agreementStore'
import TableSkeleton from '@/components/TableSkeleton'
import { useMasterData } from '@/context/MasterDataContext'

const formatDate = (date: string | Date) =>
  new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

const getDaysLeft = (endDate: string | Date): number => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

// Prefer the persisted validity period; fall back to computing it from the
// contract dates for older rows saved before the field existed.
const getValidityDays = (row: AgreementRow): number | null => {
  if (typeof row.validityDays === 'number') return row.validityDays
  if (!row.startDate || !row.endDate) return null
  const start = new Date(row.startDate)
  start.setHours(0, 0, 0, 0)
  const end = new Date(row.endDate)
  end.setHours(0, 0, 0, 0)
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
}

// "Agreement Between" — the party names, e.g. "Acme Textiles & Shakthi Power
// Solutions Pvt Ltd". Falls back to the legacy single partyName field for
// rows saved before the full parties list existed.
const getPartiesLabel = (row: AgreementRow): string => {
  if (row.parties && row.parties.length > 0) {
    return row.parties.map((p) => p.name).filter(Boolean).join(' & ')
  }
  return row.partyName || '—'
}

const AgreementList: React.FC = () => {
  const navigate = useNavigate()
  const { agreementTypes } = useMasterData()
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<AgreementStatus | undefined>()
  const [filterTemplate, setFilterTemplate] = useState<AgreementTemplate | undefined>()
  const [agreements, setAgreements] = useState<AgreementRow[]>(() => agreementStore.getAll())
  const [filterExpiringSoon, setFilterExpiringSoon] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<AgreementRow[]>([])

  // Hydrate from the DB on mount and stay live on any add / edit / status change.
  useEffect(() => {
    const update = () => setAgreements(agreementStore.getAll())
    update()
    agreementStore.refresh().then(update)
    window.addEventListener(agreementStore.eventName, update)
    return () => window.removeEventListener(agreementStore.eventName, update)
  }, [])

  const totalCount = agreements.length
  const activeCount = agreements.filter((a) => a.status === AgreementStatus.ACTIVE).length
  const pendingCount = agreements.filter(
    (a) =>
      a.status === AgreementStatus.READY_FOR_SIGNING ||
      a.status === AgreementStatus.PENDING_SCAN
  ).length
  const expiringSoonCount = agreements.filter((a) => {
    if (a.status !== AgreementStatus.ACTIVE) return false
    const daysLeft = getDaysLeft(a.endDate)
    return daysLeft >= 0 && daysLeft <= 60
  }).length

  const kpiCards = [
    {
      label: 'Total Agreements',
      value: totalCount,
      icon: <FileProtectOutlined style={{ fontSize: 24, color: 'var(--brand-primary)' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: 'var(--brand-primary)',
    },
    {
      label: 'Active',
      value: activeCount,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: 'var(--brand-green)' }} />,
      iconBg: 'rgba(0,166,81,0.08)',
      borderColor: 'var(--brand-green)',
    },
    {
      label: 'Pending Action',
      value: pendingCount,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
      iconBg: 'rgba(250,140,22,0.08)',
      borderColor: '#fa8c16',
    },
    {
      label: 'Expiring Soon',
      value: expiringSoonCount,
      icon: <ExclamationCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />,
      iconBg: 'rgba(255,77,79,0.08)',
      borderColor: '#ff4d4f',
    },
  ]

  const filtered = agreements.filter((a) => {
    const q = searchText.toLowerCase()
    const matchSearch =
      !q ||
      a.id.toLowerCase().includes(q) ||
      (a.code || '').toLowerCase().includes(q) ||
      a.leadId.toLowerCase().includes(q) ||
      a.proposalId.toLowerCase().includes(q)
    const matchStatus = !filterStatus || a.status === filterStatus
    const matchTemplate = !filterTemplate || a.templateType === filterTemplate

    let matchExpiring = true
    if (filterExpiringSoon) {
      if (a.status !== AgreementStatus.ACTIVE) {
        matchExpiring = false
      } else {
        const daysLeft = getDaysLeft(a.endDate)
        matchExpiring = daysLeft >= 0 && daysLeft <= 60
      }
    }

    return matchSearch && matchStatus && matchTemplate && matchExpiring
  })

  const renderStatusTag = (status: string) => {
    const configs: Record<
      AgreementStatus,
      { color: string; icon?: React.ReactNode }
    > = {
      [AgreementStatus.ACTIVE]: {
        color: 'success',
        icon: <CheckCircleOutlined />,
      },
      [AgreementStatus.READY_FOR_SIGNING]: {
        color: 'processing',
        icon: <SyncOutlined spin />,
      },
      [AgreementStatus.PENDING_SCAN]: {
        color: 'warning',
        icon: <ClockCircleOutlined />,
      },
      [AgreementStatus.SIGNED]: {
        color: 'blue',
        icon: <CheckCircleOutlined />,
      },
      [AgreementStatus.DRAFT]: { color: 'default' },
      [AgreementStatus.EXPIRED]: { color: 'error' },
      [AgreementStatus.AMENDED]: { color: 'cyan' },
    }
    const cfg = configs[status as AgreementStatus] ?? { color: 'default' }
    let displayText = status
    if (status === AgreementStatus.ACTIVE || status === AgreementStatus.SIGNED) displayText = 'Executed'
    if (status === AgreementStatus.READY_FOR_SIGNING || status === AgreementStatus.PENDING_SCAN) displayText = 'Waiting for Sign'
    if (status === AgreementStatus.DRAFT) displayText = 'Draft'
    
    return (
      <Tag color={cfg.color} icon={cfg.icon}>
        {displayText}
      </Tag>
    )
  }


  const columns = [
    {
      title: 'AGREEMENT CODE',
      dataIndex: 'code',
      key: 'code',
      render: (_: unknown, record: AgreementRow) => {
        const codeDisplay = record.code || record.id
        return (
          <Typography.Text
            style={{ color: 'var(--brand-primary)', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => navigate(`/agreements/${record.id}`)}
          >
            {codeDisplay}
          </Typography.Text>
        )
      },
    },
    {
      title: 'AGREEMENT DATE',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (d: string) => formatDate(d),
    },
    {
      title: 'AGREEMENT BETWEEN',
      key: 'parties',
      width: 180,
      render: (_: unknown, record: AgreementRow) => {
        const label = getPartiesLabel(record)
        return (
          <Typography.Text
            ellipsis={{ tooltip: label }}
            style={{ color: 'var(--brand-info)', fontWeight: 600, maxWidth: 180, display: 'inline-block' }}
          >
            {label}
          </Typography.Text>
        )
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status),
    },
    {
      title: 'VALIDITY',
      key: 'validity',
      render: (_: unknown, record: AgreementRow) => {
        const days = getValidityDays(record)
        if (days === null) return <span style={{ color: 'var(--text-muted)' }}>—</span>
        return <span>{(days / 365).toFixed(1)} yrs</span>
      },
    },
    {
      title: 'DAYS LEFT',
      key: 'daysLeft',
      render: (_: unknown, record: AgreementRow) => {
        if (record.status !== AgreementStatus.ACTIVE) {
          return <span style={{ color: 'var(--text-muted)' }}>—</span>
        }
        const days = getDaysLeft(record.endDate)
        if (days < 0) {
          return <Tag color="error">Expired</Tag>
        }
        return (
          <span
            style={{
              fontWeight: 600,
              color: days < 30 ? '#ff4d4f' : 'var(--brand-green)',
            }}
          >
            {days}d
          </span>
        )
      },
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      render: (_: unknown, record: AgreementRow) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/agreements/${record.id}`)}
            title="View"
          />
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/agreements/${record.id}/edit`)}
            title="Edit"
          />
          {(record.status === AgreementStatus.DRAFT ||
            record.status === AgreementStatus.READY_FOR_SIGNING ||
            record.status === AgreementStatus.PENDING_SCAN) && (
            <Button
              type="text"
              size="small"
              icon={<UploadOutlined />}
              style={{ color: '#fa8c16' }}
              title="Upload Signed Copy"
              onClick={() => navigate(`/agreements/${record.id}`)}
            />
          )}
        </Space>
      ),
    },
  ]

  if (!agreementStore.loaded) return <TableSkeleton columns={7} />

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 700,
              color: 'var(--brand-primary)',
              letterSpacing: '-0.3px',
            }}
          >
            Agreement Management
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
            Manage and track all power purchase agreements
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate('/agreements/new')}
          style={{ background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}
        >
          New Agreement
        </Button>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {kpiCards.map((card) => (
          <Col key={card.label} xs={24} sm={12} lg={6}>
            <Card
              hoverable={card.label === 'Expiring Soon'}
              onClick={() => {
                if (card.label === 'Expiring Soon') {
                  setFilterExpiringSoon(prev => !prev)
                  setFilterStatus(undefined)
                  setFilterTemplate(undefined)
                }
              }}
              style={{
                borderLeft: `3.5px solid ${card.borderColor}`,
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
                background: 'var(--surface-1)',
                cursor: card.label === 'Expiring Soon' ? 'pointer' : 'default',
                transform: filterExpiringSoon && card.label === 'Expiring Soon' ? 'scale(1.02)' : 'none',
                borderColor: filterExpiringSoon && card.label === 'Expiring Soon' ? '#ff4d4f' : undefined,
              }}
              styles={{ body: { padding: '16px 20px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: card.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Alert banner */}
      {expiringSoonCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <span>
              {expiringSoonCount} agreement(s) expiring within 60 days —{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault()
                setFilterExpiringSoon(true)
                setFilterStatus(undefined)
                setFilterTemplate(undefined)
              }} style={{ fontWeight: 600, color: '#b27a00', textDecoration: 'underline' }}>
                click here to view
              </a>
            </span>
          }
          style={{ marginBottom: 16, borderRadius: 'var(--radius-lg)', cursor: 'pointer' }}
          onClick={() => {
            setFilterExpiringSoon(true)
            setFilterStatus(undefined)
            setFilterTemplate(undefined)
          }}
        />
      )}

      {filterExpiringSoon && (
        <Alert
          type="info"
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Showing <strong>Expiring Soon</strong> agreements (expiring within 60 days)</span>
              <Button size="small" type="primary" onClick={() => setFilterExpiringSoon(false)}>
                Clear Filter
              </Button>
            </div>
          }
          style={{ marginBottom: 16, borderRadius: 'var(--radius-lg)' }}
          showIcon
        />
      )}

      {/* Filter bar */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
        }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={12} lg={10}>
            <Input.Search
              placeholder="Search by Code, Lead or Proposal..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(v) => setSearchText(v)}
            />
          </Col>
          <Col xs={24} sm={6} lg={7}>
            <Select
              placeholder="Filter by Status"
              allowClear
              value={filterStatus}
              onChange={(v) => setFilterStatus(v)}
              style={{ width: '100%' }}
              options={Object.values(AgreementStatus).map((s) => ({
                label: s,
                value: s,
              }))}
            />
          </Col>
          <Col xs={24} sm={6} lg={7}>
            <Select
              placeholder="Filter by Agreement Type"
              allowClear
              value={filterTemplate}
              onChange={(v) => setFilterTemplate(v)}
              style={{ width: '100%' }}
              options={[
                ...agreementTypes
                  .filter((a) => a.status === 'active')
                  .map((a) => ({ label: `${a.code} — ${a.name}`, value: a.name })),
                ...Object.values(AgreementTemplate).map((t) => ({ label: t, value: t })),
              ].filter((opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx)}
            />
          </Col>
        </Row>
      </Card>

      {/* Row Selection Action Bar */}
      {selectedRowKeys.length > 0 && (
        <Card
          style={{
            marginBottom: 16,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(0, 26, 77, 0.05)',
            border: '1px solid rgba(0, 26, 77, 0.15)',
          }}
          styles={{ body: { padding: '12px 20px' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, color: 'var(--brand-primary)' }}>
              {selectedRowKeys.length} agreement{selectedRowKeys.length > 1 ? 's' : ''} selected
            </span>
            <Space>
              {selectedRowKeys.length === 1 && (
                <>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => navigate(`/agreements/${selectedRowKeys[0]}/edit`)}
                  >
                    Edit Selected
                  </Button>
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => navigate(`/agreements/${selectedRowKeys[0]}`)}
                  >
                    View Selected
                  </Button>
                </>
              )}
              <Button size="small" onClick={() => { setSelectedRowKeys([]); setSelectedRows([]); }}>
                Deselect All
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card
        style={{
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
        }}
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys)
              setSelectedRows(rows)
            },
          }}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} agreements`,
          }}
          style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
        />
      </Card>
    </div>
  )
}

export default AgreementList
